import React from 'react'
import { Container, Stack, Title, Text, Button, Center, Box } from '@mantine/core'
import { IconRefresh, IconExclamationCircle } from '@tabler/icons-react'

interface ErrorBoundaryState {
    hasError: boolean
    error?: Error
}

interface ErrorBoundaryProps {
    children: React.ReactNode
    fallback?: React.ComponentType<{ error?: Error; resetError: () => void }>
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
    constructor(props: ErrorBoundaryProps) {
        super(props)
        this.state = { hasError: false }
    }

    static getDerivedStateFromError(error: Error): ErrorBoundaryState {
        return { hasError: true, error }
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
        console.error('Error caught by boundary:', error, errorInfo)
    }

    resetError = () => {
        this.setState({ hasError: false, error: undefined })
    }

    render() {
        if (this.state.hasError) {
            if (this.props.fallback) {
                const FallbackComponent = this.props.fallback
                return <FallbackComponent error={this.state.error} resetError={this.resetError} />
            }

            return <DefaultErrorFallback error={this.state.error} resetError={this.resetError} />
        }

        return this.props.children
    }
}

interface ErrorFallbackProps {
    error?: Error
    resetError: () => void
}

function DefaultErrorFallback({ resetError }: ErrorFallbackProps) {
    const handleRefresh = () => {
        resetError()
        window.location.reload()
    }

    return (
        <Container size="sm" h="100vh">
            <Center h="100%">
                <Box ta="center">
                    <Stack align="center" gap="xl">
                        <IconExclamationCircle size={80} color="var(--mantine-color-red-6)" />

                        <Stack align="center" gap="md">
                            <Title order={2} size="h3">
                                Whoops! Something went wrong
                            </Title>

                            <Text size="md" c="dimmed" ta="center" maw={400}>
                                We're working hard to fix it, please check again later.
                            </Text>
                        </Stack>

                        <Button
                            leftSection={<IconRefresh size={16} />}
                            onClick={handleRefresh}
                            size="md"
                            variant="filled"
                        >
                            Try Again
                        </Button>
                    </Stack>
                </Box>
            </Center>
        </Container>
    )
} 